import { getJsonBody, handleFailureAsObject } from '@/api/apiHelpers';
import type { PhoneNumber, RentalStatusPageType, UpdateStatusAction } from '@/domain/transaction';
import { type AmendedPricePreview, parseAmendedPricePreview } from '@/models/amendedPricePreview';
import { type BookingArchive, createArchiveData } from '@/models/bookingArchive';
import { type BooleanApiResponse, parseApiResponse } from '@/models/booleanApiResponse';
import { createRentalStatusState, type RentalStatusType } from '@/models/rentalStatus';

const urlEncodeBody = (body: object): string =>
    Object.keys(body)
        .filter((key) => Boolean(body[key]))
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(body[key])}`)
        .join('&');

export const postTenantDisputeData = async (
    transactionExternalUrl: string,
    bookingReference: string,
    data: FormData,
): Promise<BooleanApiResponse> =>
    fetch(`${transactionExternalUrl}/dispute/tenant/${bookingReference}`, {
        method: 'POST',
        credentials: 'include',
        body: data,
    })
        .then(getJsonBody)
        .then(parseApiResponse);

export const getAmendedPricePreview = async (
    transactionExternalUrl: string,
    bookingReference: string,
    discount: number,
    type: RentalStatusPageType,
): Promise<AmendedPricePreview> =>
    fetch(`${transactionExternalUrl}/rental-status/${type}/${bookingReference}/amend?discount=${discount}`)
        .then(getJsonBody)
        .then(parseAmendedPricePreview);

export const updateRentalStatus = async (
    transactionExternalUrl: string,
    bookingReference: string,
    type: RentalStatusPageType,
    action: UpdateStatusAction,
    body?: Record<string, unknown>,
): Promise<{ status: RentalStatusType }> =>
    fetch(`${transactionExternalUrl}/rental-status/${type}/${bookingReference}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: urlEncodeBody(body ?? {}),
    })
        .then(handleFailureAsObject)
        .then(createRentalStatusState);

export const createRentalRequest = async (transactionExternalUrl: string, params: string): Promise<string | null> =>
    fetch(`${transactionExternalUrl}/rental?${params}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
    })
        .then((response) => (response.ok ? response.json().then((body) => Promise.resolve(body.bookingReference)) : Promise.resolve(null)))
        .catch(() => null);

export const getPaymentUrl = async (
    transactionExternalUrl: string,
    bookingReference: string,
    paymentOption: string,
): Promise<string | null> =>
    fetch(`${transactionExternalUrl}/payment?paymentOption=${paymentOption}&bookingReference=${bookingReference}`, {
        method: 'POST',
        credentials: 'include',
    })
        .then((response) =>
            response.ok
                ? Promise.resolve(response.headers.get('Location'))
                : Promise.reject(`(${response.status}) ${response.statusText}: ${response.url}`),
        )
        .catch(() => null);

export const findOverlappingRentalRequests = async (transactionExternalUrl: string, bookingReference: string): Promise<number> =>
    fetch(`${transactionExternalUrl}/rental/host/${bookingReference}/overlapping`, { credentials: 'include' })
        .then(getJsonBody)
        .then(Number);

export const getBookingArchiveData = async (transactionExternalUrl: string, type: RentalStatusPageType): Promise<BookingArchive[]> =>
    fetch(`${transactionExternalUrl}/archive/${type}/all`).then(getJsonBody).then(createArchiveData);

export const createCancelReason = async (
    transactionExternalUrl: string,
    bookingReference: string,
    role: RentalStatusPageType,
    reason: string[],
): Promise<BooleanApiResponse> =>
    await fetch(`${transactionExternalUrl}/cancel-reason`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ bookingReference: bookingReference, role: role, reason: reason }),
    })
        .then(getJsonBody)
        .then(parseApiResponse);

export const updateHostPhoneNumber = async (
    transactionExternalUrl: string,
    phoneCountryCode: string,
    phoneNumber: string,
): Promise<PhoneNumber> =>
    fetch(`${transactionExternalUrl}/contact/phone?phoneCountryCode=${phoneCountryCode}&phoneNumber=${phoneNumber}`, {
        method: 'POST',
        credentials: 'include',
    }).then(getJsonBody);
